import { supabase } from '../config/supabase';

const DEFAULT_MODEL = 'gpt-5';

/**
 * Récupère le nom de la commune depuis le code postal
 * @param {string} postalCode - Le code postal
 * @returns {Promise<string|null>} Le nom de la commune ou null
 */
export const getCommuneName = async (postalCode) => {
  try {
    const { data, error } = await supabase
      .from('communes')
      .select('nom_de_la_commune')
      .eq('code_postal', postalCode)
      .limit(1);
    
    if (error || !data || data.length === 0) {
      return null;
    }
    
    return data[0].nom_de_la_commune;
  } catch (error) {
    console.error('[getCommuneName] Erreur:', error);
    return null;
  }
};

/**
 * Génère le prompt pour l'import d'événements
 * @param {string} postalCode - Le code postal
 * @returns {Promise<string>} Le prompt généré
 */
export const generateEventPrompt = async (postalCode) => {
  const communeName = await getCommuneName(postalCode);
  const villeText = communeName ? communeName : `du code postal ${postalCode}`;
  
  return `Consulte le site officiel de la mairie de ${villeText} en France.
Recherche tous les événements d'aujourd'hui ou dans le futur.
Réponds obligatoirement en JSON compact avec uniquement les champs suivants :

{
  "title": "...",
  "description": "...",
  "image_url": "...",
  "category":- à respecter strictement : [culture,art,music, theater, cinema, festival, sport, workshop, market, conference, nature, family],
  "age_group": - à respecter strictement : [under3, 3to7, 8to12,12to15,15to18,adults],
  "is_paid": true/false,
  "external_url": "...",
  "is_accessible": true/false,
  "weather_dependent": true/false,
  "location_name": "...",
  "formatted_address": "..." sans code postal et sans ville,
  "postal_code": "...",
  "city": "..." ville uniquement,
  "start_date": "YYYY-MM-DD",
  "end_date": "YYYY-MM-DD",
  "start_time": "HH:MM",
  "end_time": "HH:MM",
  "timezone": "Europe/Paris",
  "organizer_url": "url"
}

Ne fournis **aucun texte explicatif**, uniquement un tableau JSON d'objets.
N'utilisr pas de '\\n' dans les champs.
Donne un champs title de 150 caractères maximum.
Si une donnée est inconnue, mets "null".`;
};

/**
 * Crée un job d'import pour un code postal
 * @param {string} userId - L'ID de l'utilisateur
 * @param {string} postalCode - Le code postal
 * @param {string} [model] - Le modèle à utiliser (optionnel)
 * @returns {Promise<Object>} Le job créé
 */
export const createImportJob = async (userId, postalCode, model = DEFAULT_MODEL) => {
  const prompt = await generateEventPrompt(postalCode);
  
  const { data, error } = await supabase
    .from('import_jobs')
    .insert({
      user_id: userId,
      source: source,
      prompt: prompt,
      status: 'pending',
      model: model
    })
    .select()
    .single();
  
  if (error) {
    console.error('[createImportJob] Erreur Supabase:', error);
    throw error;
  }
  
  return data;
};

/**
 * Vérifie les jobs existants pour une liste de codes postaux
 * @param {Array<string>} postalCodes - Les codes postaux à vérifier
 * @param {number} [freshnessDays=30] - Nombre de jours de fraîcheur
 * @returns {Promise<Array>} Les jobs existants
 */
export const checkExistingJobs = async (postalCodes, freshnessDays = 30) => {
  const freshnessDate = new Date();
  freshnessDate.setDate(freshnessDate.getDate() - freshnessDays);
  const dateLimit = freshnessDate.toISOString();

  const { data, error } = await supabase
    .from('import_jobs')
    .select('source, status, created_at')
    .in('source', postalCodes)
    .gte('created_at', dateLimit);

  if (error) {
    console.error('[checkExistingJobs] Erreur Supabase:', error);
    return [];
  }

  return data || [];
};

/**
 * Vérifie s'il existe un job récent pour un code postal
 * @param {string} postalCode - Le code postal
 * @param {number} [monthsAgo=1] - Nombre de mois à vérifier
 * @returns {Promise<boolean>} True si un job récent existe
 */
export const hasRecentJob = async (postalCode, monthsAgo = 1) => {
  if (!postalCode) {
    return false;
  }

  const limitDate = new Date();
  limitDate.setMonth(limitDate.getMonth() - monthsAgo);
  const dateLimit = limitDate.toISOString();

  const { data, error } = await supabase
    .from('import_jobs')
    .select('id, status, created_at, source')
    .eq('source', postalCode)
    .gte('created_at', dateLimit)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[hasRecentJob] Erreur Supabase:', error);
    return false;
  }

  return data && data.length > 0;
};

/**
 * Vérifie s'il existe un job récent pour un code postal basé sur freshness
 * @param {string} postalCode - Le code postal
 * @param {number} freshnessDays - Nombre de jours de fraîcheur
 * @returns {Promise<boolean>} True si un job récent existe
 */
export const hasRecentJobByFreshness = async (postalCode, freshnessDays) => {
  if (!postalCode || !freshnessDays) {
    return false;
  }

  const limitDate = new Date();
  limitDate.setDate(limitDate.getDate() - freshnessDays);
  const dateLimit = limitDate.toISOString();

  const { data, error } = await supabase
    .from('import_jobs')
    .select('id, status, created_at, source')
    .eq('source', postalCode)
    .eq('status', 'completed')
    .gte('created_at', dateLimit)
    .order('created_at', { ascending: false })
    .limit(1);

  if (error) {
    console.error('[hasRecentJobByFreshness] Erreur Supabase:', error);
    return false;
  }

  return data && data.length > 0;
};

/**
 * Récupère toutes les communes avec include=true
 * @returns {Promise<Array>} Les communes à inclure avec leur freshness
 */
export const getCommunesToInclude = async () => {
  const { data, error } = await supabase
    .from('communes')
    .select('code_postal, freshness')
    .eq('include', true);

  if (error) {
    console.error('[getCommunesToInclude] Erreur Supabase:', error);
    return [];
  }

  return data || [];
};
